/**
 * Per-species abilities.
 *
 * Each one traces to a specific thing Kate said:
 *   Sniff       "if you're a wolf, you can crouch down and sniff... it's like there's a
 *                little green trail and that leads to your prey and other players."
 *                And on duration: "You can hold on crouch for however long you want."
 *   Fly         "you can fly... You can't fly very high when you're a bird. You can only
 *                fly as high as the trees."
 *   Climb+kill  "what if you're a cat? You can climb trees and kill the bird."
 *   Camouflage  "there's like lizards, chameleons. You can camouflage."
 */
import * as THREE from 'three'
import type { AnimalAgent } from './agent'
import { has, type Species } from './species'
import type { TreeGrid } from '../world/trees'

/** How far a wolf can smell. */
export const SNIFF_RANGE = 45
/** A bird may not climb above the canopy; this is the fallback where no tree is near. */
export const DEFAULT_TREE_HEIGHT = 9
/** How close to a trunk you must be to climb it. */
export const CLIMB_RANGE = 1.8

/**
 * The green trail. Kate described it as a trail leading to prey, so it is drawn as a
 * line along the ground from the wolf to the nearest living animal.
 */
export class SniffTrail {
  readonly line: THREE.Line
  /** The animal currently being tracked, or null. */
  target: AnimalAgent | null = null
  visible = false

  constructor() {
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(2 * 3), 3))
    this.line = new THREE.Line(
      geometry,
      new THREE.LineBasicMaterial({ color: 0x5cff6a, transparent: true, opacity: 0.85 }),
    )
    this.line.frustumCulled = false
    this.line.visible = false
  }

  /**
   * Show the trail only for a wolf that is crouching. Any other species, or a wolf
   * standing up, hides it.
   */
  update(species: Species, crouching: boolean, x: number, z: number,
         animals: readonly AnimalAgent[]): void {
    this.visible = has(species, 'sniff') && crouching
    this.line.visible = this.visible
    if (!this.visible) {
      this.target = null
      return
    }

    let nearest: AnimalAgent | null = null
    let nearestDistance = SNIFF_RANGE
    for (const animal of animals) {
      if (animal.isDead()) continue
      const distance = Math.hypot(animal.x - x, animal.z - z)
      if (distance < nearestDistance) {
        nearest = animal
        nearestDistance = distance
      }
    }

    this.target = nearest
    if (!nearest) {
      this.line.visible = false
      return
    }

    const position = this.line.geometry.getAttribute('position') as THREE.BufferAttribute
    position.setXYZ(0, x, 0.15, z)
    position.setXYZ(1, nearest.x, 0.15, nearest.z)
    position.needsUpdate = true
  }
}

/**
 * The ceiling a bird may fly to: the canopy of the nearest tree, or a default where
 * there is none. Kate capped flight at tree height so the cat stays a real counter.
 */
export function flightCeiling(x: number, z: number, trees: TreeGrid): number {
  let best = DEFAULT_TREE_HEIGHT
  let bestDistance = Infinity
  for (const tree of trees.near(x, z)) {
    const distance = Math.hypot(tree.x - x, tree.z - z)
    if (distance < bestDistance) {
      bestDistance = distance
      best = tree.canopyHeight
    }
  }
  return best
}

/** The trunk close enough to climb, if any. */
export function climbableTree(x: number, z: number, trees: TreeGrid) {
  for (const tree of trees.near(x, z)) {
    if (Math.hypot(tree.x - x, tree.z - z) <= tree.radius + CLIMB_RANGE) return tree
  }
  return null
}

/**
 * Camouflage strength, 0..1. Kate named the chameleon her hide-and-seek animal, so it
 * fades in when still and breaks when moving — hiding, not invisibility on demand.
 */
export class Camouflage {
  /** 0 = fully visible, 1 = fully blended. */
  strength = 0

  update(dt: number, species: Species, moving: boolean): void {
    if (!has(species, 'camouflage')) {
      this.strength = 0
      return
    }
    // Roughly 1.5s to disappear, and a third of a second to give yourself away.
    const target = moving ? 0 : 1
    const rate = moving ? 3 : 0.66
    this.strength += Math.sign(target - this.strength) * Math.min(
      Math.abs(target - this.strength), rate * dt,
    )
  }

  /** Opacity the body should render at. */
  get opacity(): number {
    return 1 - this.strength * 0.92
  }
}

/**
 * Kate's one explicit predation rule: the cat is the bird's counter. Anything may bite
 * anything, but a bird is killed outright only by a cat.
 */
export function killsInstantly(attacker: Species, targetSpecies: string): boolean {
  return has(attacker, 'killBirds') && targetSpecies === 'bird'
}
